import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Col,
  Container,
  Row,
  Card
} from 'reactstrap';
import Carousel from '../Carousel';
import { onSelectAddOnCarouseHandler } from '../../utils/products';
import Summary from '../Summary';
import { PaymentGateways, RulesName, RulesType } from '../../enums';
import { useResponsiveValue } from '@theme-ui/match-media';
import { MotorplayPlanCarousel, PlanCard } from '../ProductSelectionPage/motorplay/components';

const AddOnPlus = ({
  history,
  intl,
  user,
  client,
  applyProductRules,
  addOnSubscriptions,
  setAddOnsToShow,
  addOnsToShow,
  addOnList,
  productsToBuy,
  addProduct,
  subscriptions,
  setIgnoreRedirect,
  setTotalPriceSummary,
  subPaymentMethods,
  setCurrentSubPaymentMethod,
  cleanOrder,
  isSingleSubscriptionClient,
  userPlan,
}) => {
  const numberOfCards = useResponsiveValue([1, 2, 2]);
  const slidesToScroll = useResponsiveValue([1, 1, 2, 2]);
  const gutter = useResponsiveValue([10, 10, 12]);
  const showSlither = useResponsiveValue([false, true, false]);
  const outsideChevron = useResponsiveValue([false, true, false]);
  const [query, setQuery] = useState(null);
  const addOnProducts = addOnsToShow || addOnList;
  const isMotorplay = client === 'motorplay';

  useEffect(() => {
    setIgnoreRedirect(true);
    cleanOrder();
  }, []);

  useEffect(() => {
    applyProductRules({
      ruleType: RulesType.ADDITIONAL_PURCHASE,
      ruleName: RulesName.INCOMPATIBLE_OFFERINGS,
      productId: addOnSubscriptions.map(subscription => subscription.productId)
    });
    applyProductRules({ ruleType: RulesType.PURCHASE, ruleName: RulesName.PLAN_INCLUDE_ADDONS, productId: userPlan.productId });
  }, []);

  useEffect(() => {
    if (query) return;

    const params = new URLSearchParams(history.location.search);
    const paramsObject = Object.fromEntries(params.entries());

    setQuery(paramsObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.search]);

  const handleContinueClick = () => {
    const currentSubPaymentMethod = subPaymentMethods.find(sub => sub.countryCode.toLowerCase() === query.country.toLowerCase() && sub.active);

    setCurrentSubPaymentMethod(currentSubPaymentMethod);
    
    const correctRedirectUrl = currentSubPaymentMethod.paymentMethod === PaymentGateways.mercadopago ? 
    `/${client}/subscribe/methods/${currentSubPaymentMethod.paymentMethod}/${currentSubPaymentMethod.type}${history.location.search}` : 
    `/${client}/subscribe/my-payment-methods${history.location.search}`;
    history.push(correctRedirectUrl);
  }

  if (isSingleSubscriptionClient) {
    history.replace(`/${client}/subscribe/subscriptions/myaccount${history.location.search}`);
  }

  const renderCarousel = () => {
    if (isMotorplay) {
      return (
        <MotorplayPlanCarousel
          products={addOnProducts}
          numberOfCards={numberOfCards}
          renderCard={({ product, productId, isDescriptionExpanded, onDescriptionExpandChange }) => (
            <PlanCard
              key={productId}
              plan={product}
              selected={product.isSelected}
              isBlocked={product.isBlocked}
              isDescriptionExpanded={isDescriptionExpanded}
              onDescriptionExpandChange={onDescriptionExpandChange}
              onSelect={() => {
                onSelectAddOnCarouseHandler(
                  product,
                  addOnProducts,
                  setAddOnsToShow,
                  addProduct,
                  true
                );
              }}
            />
          )}
        />
      );
    }

    return (
      <Carousel
        intl={intl}
        userPlan={userPlan}
        client={client}
        carouselOptions={{
          gutter, numberOfCards, showSlither, outsideChevron, slidesToScroll
        }}
        onSelect={(addOn) => {
          onSelectAddOnCarouseHandler(addOn, addOnProducts, setAddOnsToShow, addProduct, true);
        }}
        addOnSubscriptions={subscriptions?.addOns}
        exchangeRate={user.exchangeRate}
        products={addOnProducts}
        user={user}
      />
    );
  };

  return (
    <Container className={`PageCard tbx-addOn-plus ${isMotorplay ? 'MembershipSelectionPage' : 'container-fluid'}`}>
      {!isMotorplay && (
        <>
          <Row>
            <Col>
              <h1 className="myAccount__subscription_title">
                <FormattedMessage
                  id="MyAccount.title"
                />
              </h1>
            </Col>
          </Row>
          <Row>
            <Col>
              <h2 className="tbx-addOn-plus__title">
                <FormattedMessage id="MyAccount.subscription.addOnPlus.title" />
              </h2>
            </Col>
          </Row>
        </>
      )}
      <Row className={`tbx-addOn-plus__container${isMotorplay ? '' : ' mt-5'}`}>
        <Col md={12} lg={7} className={isMotorplay ? 'tbx-carousel-addOns-container' : undefined}>
          <Card className="tbx-card-carousel" style={{ backgroundColor: '#000000bd' }}>
            {renderCarousel()}
          </Card>
        </Col>
        <Col md={12} lg={5} className="summary-content left">
          <Summary
              selectedProducts={productsToBuy || []}
              handleContinueButton={handleContinueClick}
              subscriptions={subscriptions}
              user={user}
              setPriceToShow={setTotalPriceSummary}
              isAddOnProduct
          />
        </Col>
      </Row>
    </Container>);
}

AddOnPlus.propTypes = {
  addOnList: PropTypes.array.isRequired,
  addOnsToShow: PropTypes.array,
  history: PropTypes.object,
  intl: PropTypes.any,
  user: PropTypes.object,
  client: PropTypes.string,
  applyProductRules: PropTypes.func,
  setAddOnsToShow: PropTypes.func,
  setIgnoreRedirect: PropTypes.func.isRequired,
  addProduct: PropTypes.func.isRequired,
  cleanOrder: PropTypes.func.isRequired,
  productsToBuy: PropTypes.array,
  subscriptions: PropTypes.object,
  addOnSubscriptions: PropTypes.array,
  isSingleSubscriptionClient: PropTypes.bool.isRequired,
  userPlan: PropTypes.object,
};

export default AddOnPlus;
